import { Icon } from '@/shared/components/ui/icon';
import { cn } from '@/shared/lib/utils';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import * as React from 'react';

/**
 * Figma 라이브러리 Context Menu (804:3272) · Context Menu (5 Rows) 묶음
 * - 목록: 흰 배경 · radius 20 · 위아래 8 · 그림자 500 · 기본 폭 240 (정렬 드롭다운은 className="w-[360px]")
 * - 항목: 높이 48 · 좌우 16 · 아이콘 칸 16 + 간격 12 · 15 Medium
 *   selected: 체크 아이콘 + 15 SemiBold 파랑 / tone="negative": 15 SemiBold 빨강
 *   hover brand-75 · press brand-100
 * 트리거는 보통 <Button variant="outline" rightIcon={<Icon name="nav-arrow-down-line-24" />}> (열리면 테두리 grey-500)
 */
const DropdownMenu = DropdownMenuPrimitive.Root;
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
const DropdownMenuGroup = DropdownMenuPrimitive.Group;

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 8, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-50 flex min-w-60 flex-col overflow-hidden rounded-20 bg-surface-white py-2 shadow-500 outline-none',
        className,
      )}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

interface DropdownMenuItemProps extends React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.Item
> {
  /** 선택된 항목 — 체크 아이콘 + 파란 SemiBold (Figma State=Selected) */
  selected?: boolean;
  /** negative: 빨간 SemiBold (삭제 · 로그아웃 등, Figma State=Negative) */
  tone?: 'default' | 'negative';
  /** 아이콘 칸에 넣을 16px 아이콘. selected면 체크 아이콘이 우선한다 */
  icon?: React.ReactNode;
  /** false면 아이콘 칸(16 + 12)을 두지 않는다 */
  iconSlot?: boolean;
}

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  DropdownMenuItemProps
>(
  (
    {
      className,
      selected,
      tone = 'default',
      icon,
      iconSlot = true,
      children,
      ...props
    },
    ref,
  ) => (
    <DropdownMenuPrimitive.Item
      ref={ref}
      aria-checked={selected}
      className={cn(
        'flex h-12 shrink-0 cursor-pointer items-center gap-3 px-4 text-body-15-m text-fg-primary outline-none select-none',
        'data-[highlighted]:bg-brand-75 active:bg-brand-100 data-[disabled]:pointer-events-none data-[disabled]:text-fg-disabled',
        selected && 'text-body-15-sb text-fg-accent',
        tone === 'negative' && 'text-body-15-sb text-fg-error',
        className,
      )}
      {...props}
    >
      {iconSlot && (
        <span className="flex size-4 shrink-0 items-center justify-center">
          {selected ? <Icon name="check-line-16" /> : icon}
        </span>
      )}
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </DropdownMenuPrimitive.Item>
  ),
);
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

export {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
};
